import type { CSSProperties, ReactNode } from "react";
import { Link } from "react-router-dom";
import { ApiError } from "../../shared/api/client";
import { formatTenantDate, formatTenantDateTime } from "../../shared/lib/tenantDateTime";

export const billingPageLinks = [
  { to: "/t/:tenantSlug/teacher/admin/billing", label: "請求設定" },
  { to: "/t/:tenantSlug/teacher/admin", label: "管理ホーム" }
];

export const billingGridStyle = { display: "grid", gap: 16 } satisfies CSSProperties;
export const billingCardStyle = {
  display: "grid",
  gap: 10,
  padding: 16,
  border: "1px solid #dbe4ea",
  borderRadius: 14,
  background: "#fff"
} satisfies CSSProperties;
export const billingInputStyle = {
  width: "100%",
  boxSizing: "border-box",
  padding: "10px 12px",
  border: "1px solid #b7c4cc",
  borderRadius: 8,
  background: "#fff"
} satisfies CSSProperties;
export const billingButtonStyle = {
  padding: "10px 14px",
  border: 0,
  borderRadius: 9,
  background: "#176b55",
  color: "#fff",
  fontWeight: 700,
  cursor: "pointer"
} satisfies CSSProperties;
export const billingSecondaryButtonStyle = {
  ...billingButtonStyle,
  background: "#e4eeeb",
  color: "#163e34"
} satisfies CSSProperties;
export const billingMutedStyle = { margin: 0, color: "#667085", fontSize: 14 } satisfies CSSProperties;

export function formatBillingYen(amount: number): string {
  return amount === 0 ? "無料（0円）" : `${amount.toLocaleString("ja-JP")}円`;
}

export function formatSignedBillingYen(amount: number): string {
  const sign = amount > 0 ? "+" : "−";
  return `${sign}${Math.abs(amount).toLocaleString("ja-JP")}円`;
}

export function formatBillingDate(value: string): string {
  return formatTenantDate(value);
}

export function formatBillingDateTime(value: string): string {
  return formatTenantDateTime(value);
}

export function studentBillingConfigurationError(error: unknown, fallback: string): string {
  if (!(error instanceof ApiError)) return fallback;
  switch (error.code) {
    case "STUDENT_BILLING_READ_DISABLED":
      return "生徒請求の閲覧機能は現在無効です。既存の管理画面機能は引き続き利用できます。";
    case "STUDENT_BILLING_WRITES_DISABLED":
      return "生徒請求の更新機能は現在無効です。内容は変更されていません。";
    case "STUDENT_BILLING_OWNER_REQUIRED":
    case "STUDENT_BILLING_OWNER_AUTHORITY_UNRESOLVED":
    case "STUDENT_BILLING_OWNER_AUTHORITY_CONFLICT":
      return "この設定を変更できるのはテナントオーナーだけです。";
    case "STUDENT_BILLING_VERSION_MISMATCH":
    case "STUDENT_BILLING_FEE_VERSION_MISMATCH":
      return "他の操作で情報が更新されました。再読み込みして内容を確認してください。";
    case "STUDENT_BILLING_LESSON_FEE_OVERLAP":
      return "このレッスンには重複する料金適用期間があります。既存期間を確認し、重ならない期間を指定してください。";
    case "STUDENT_BILLING_COMMERCIAL_STATE_NOT_WRITABLE":
    case "STUDENT_BILLING_COMMERCIAL_STATE_NOT_READY":
      return "現在の契約状態では、この請求設定を変更できません。";
    case "STUDENT_BILLING_MANUAL_TARGET_CHANGED":
      return "確認後に対象者が変わりました。対象を再確認してから、もう一度プレビューしてください。";
    case "STUDENT_BILLING_MANUAL_TARGET_LIMIT":
    case "STUDENT_BILLING_MANUAL_ENROLLMENT_LIMIT":
      return "対象者数が上限を超えています。対象を分けて登録してください。";
    case "STUDENT_BILLING_MANUAL_TARGET_EMPTY":
      return "この対象月に請求対象となる生徒がいません。対象と所属期間を確認してください。";
    case "STUDENT_BILLING_EVENT_FEE_ITEM_LIMIT":
      return "イベントに登録できる料金項目の上限に達しました。既存項目を確認してください。";
    case "STUDENT_BILLING_VALIDATION_FAILED":
      return "入力内容を確認してください。対象月は YYYY-MM 形式で指定してください。";
    default:
      return fallback;
  }
}

export function BillingConfigurationNav({ tenantSlug }: { tenantSlug: string }) {
  const serviceMonth = new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Tokyo", year: "numeric", month: "2-digit" }).format(new Date());
  const links = [
    ["支払担当・請求方法", `/t/${tenantSlug}/teacher/admin/billing`],
    ["レッスン料金設定", `/t/${tenantSlug}/teacher/admin/billing/lesson-fees`],
    ["イベント料金設定", `/t/${tenantSlug}/teacher/admin/billing/event-fees`],
    ["単発請求・調整", `/t/${tenantSlug}/teacher/admin/billing/manual-items`],
    ["月次請求一覧", `/t/${tenantSlug}/teacher/admin/billing/months/${serviceMonth}`]
  ] as const;
  return (
    <nav aria-label="請求設定メニュー" style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
      {links.map(([label, to]) => <Link key={to} to={to}>{label}</Link>)}
    </nav>
  );
}

export function BillingStatusBadge({ children, tone = "neutral" }: { children: ReactNode; tone?: "neutral" | "success" | "warning" }) {
  const colors = tone === "success"
    ? { background: "#e8f7ef", color: "#145c45" }
    : tone === "warning"
      ? { background: "#fff4d8", color: "#765300" }
      : { background: "#edf2f7", color: "#44515c" };
  return <span style={{ ...colors, borderRadius: 999, padding: "4px 9px", fontSize: 13, fontWeight: 700, width: "fit-content" }}>{children}</span>;
}
